/**
 * Card Comments Panel
 *
 * Unified comments UI for board-card drawers (products, …). Wires the shared
 * `/cards/:id/comments` surface into the same `CommentsTab` presentation the
 * task drawer uses — avatars, relative timestamps, mention-aware editor —
 * resolving each comment's author against the org member directory.
 *
 * @module features/cards/components/CardCommentsPanel
 */

import React from 'react';
import { CommentsTab } from '../../../ui/components/issues/tabs/CommentsTab';
import { useOrgMembers } from '../../../hooks/useOrgMembers';
import { useCardComments, useCreateCardComment } from '../hooks/useCards';

interface CardCommentsPanelProps {
  cardId: string;
  className?: string;
}

export function CardCommentsPanel({ cardId, className }: CardCommentsPanelProps) {
  const commentsQuery = useCardComments(cardId);
  const createComment = useCreateCardComment(cardId);
  const membersQuery = useOrgMembers();
  const members = membersQuery.data?.items ?? [];
  const [commentText, setCommentText] = React.useState('');

  const comments = React.useMemo(
    () =>
      (commentsQuery.data ?? []).map((c) => {
        const member = members.find((m) => m.userId === c.authorId);
        return {
          id: c.id,
          body: c.body,
          createdAt: c.createdAt,
          author: member
            ? { displayName: member.displayName, primaryEmail: member.primaryEmail }
            : null,
        };
      }),
    [commentsQuery.data, members]
  );

  return (
    <CommentsTab
      comments={comments}
      pagination={undefined}
      page={1}
      isLoading={commentsQuery.isLoading}
      isError={commentsQuery.isError}
      isFetching={commentsQuery.isFetching}
      commentText={commentText}
      isPosting={createComment.isPending}
      onCommentTextChange={setCommentText}
      onAddComment={() => {
        const trimmed = commentText.trim();
        if (!trimmed) return;
        createComment.mutate(trimmed, { onSuccess: () => setCommentText('') });
      }}
      onPageChange={() => {}}
      className={className}
      mentionableMembers={members}
    />
  );
}
